import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { CONTROL_INPUT, MUTED_LABEL } from './daily-entry-ui.js';

const ABSENCE_OPTIONS = [
  { value: 'calloff', label: 'Call Off' },
  { value: 'ncns', label: 'NCNS' },
  { value: 'pto', label: 'PTO' },
  { value: 'sick', label: 'Sick' },
  { value: 'other', label: 'Other' },
];

interface WorkedForm {
  terminalName: string;
  routeNumber: string;
  deliveries: string;
  pickups: string;
  miles: string;
  truckNumber: string;
}

interface DailyEntryActionPanelProps {
  activeAction: string | null;
  selectedDriver: { first_name?: string; last_name?: string } | null;
  clockStatus: { clock_in?: string; clocked_in?: boolean } | null;
  submitting: boolean;
  actionError: string | null;
  successMessage: string | null;
  absenceReason: string;
  absenceNotes: string;
  workedForm: WorkedForm;
  onWorkedFormChange: (field: string, value: string) => void;
  onAbsenceReasonChange: (value: string) => void;
  onAbsenceNotesChange: (value: string) => void;
  onSubmitWorked: () => void;
  onSubmitAbsent: () => void;
  onSubmitEdit: () => void;
  onClose: () => void;
}

export default function DailyEntryActionPanel({
  activeAction,
  selectedDriver,
  clockStatus,
  submitting,
  actionError,
  successMessage,
  absenceReason,
  absenceNotes,
  workedForm,
  onWorkedFormChange,
  onAbsenceReasonChange,
  onAbsenceNotesChange,
  onSubmitWorked,
  onSubmitAbsent,
  onSubmitEdit,
  onClose,
}: DailyEntryActionPanelProps) {
  if (!activeAction || !selectedDriver) {
    if (!successMessage) return null;
    return (
      <div style={LAYOUT.subPanel}>
        <div style={{ color: C.green, fontSize: 13 }}>{successMessage}</div>
      </div>
    );
  }

  const clockLabel = clockStatus?.clock_in
    ? clockStatus.clocked_in
      ? 'Clocked in'
      : 'Clocked out'
    : 'No clock record';

  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 6 }}>
            Daily Entry action
          </div>
          <div style={{ color: C.text, fontSize: 15, fontWeight: 600 }}>{selectedDriver.first_name} {selectedDriver.last_name}</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>{activeAction === 'worked' ? 'Worked shell' : activeAction === 'edit' ? 'Edit entry' : 'Absent shell'} · {clockLabel}</div>
        </div>
        <button type="button" onClick={onClose} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: C.panel, color: C.text }}>
          Close
        </button>
      </div>

      {actionError ? <div style={{ color: C.red, marginBottom: 12, fontSize: 13 }}>{actionError}</div> : null}

      {activeAction === 'worked' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={MUTED_LABEL}>Terminal</label>
            <input type="text" value={workedForm.terminalName} onChange={(event) => onWorkedFormChange('terminalName', event.target.value)} placeholder="Enter terminal name" style={CONTROL_INPUT} />
          </div>
          <div>
            <label style={MUTED_LABEL}>Route</label>
            <input type="text" value={workedForm.routeNumber} onChange={(event) => onWorkedFormChange('routeNumber', event.target.value)} placeholder="Enter route number" style={CONTROL_INPUT} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <div>
              <label style={MUTED_LABEL}>Deliveries</label>
              <input type="number" value={workedForm.deliveries} onChange={(event) => onWorkedFormChange('deliveries', event.target.value)} style={CONTROL_INPUT} />
            </div>
            <div>
              <label style={MUTED_LABEL}>Pickups</label>
              <input type="number" value={workedForm.pickups} onChange={(event) => onWorkedFormChange('pickups', event.target.value)} style={CONTROL_INPUT} />
            </div>
            <div>
              <label style={MUTED_LABEL}>Miles</label>
              <input type="number" value={workedForm.miles} onChange={(event) => onWorkedFormChange('miles', event.target.value)} style={CONTROL_INPUT} />
            </div>
            <div>
              <label style={MUTED_LABEL}>Truck #</label>
              <input type="text" value={workedForm.truckNumber} onChange={(event) => onWorkedFormChange('truckNumber', event.target.value)} style={CONTROL_INPUT} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onSubmitWorked}
              disabled={submitting}
              style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: C.gold, color: '#111', fontWeight: 700, cursor: submitting ? 'wait' : 'pointer' }}
            >
              {submitting ? 'Saving...' : 'Save worked entry'}
            </button>
            <button type="button" onClick={onClose} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: C.panel, color: C.text }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {activeAction === 'edit' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={MUTED_LABEL}>Terminal</label>
            <input type="text" value={workedForm.terminalName} onChange={(event) => onWorkedFormChange('terminalName', event.target.value)} placeholder="Terminal name" style={CONTROL_INPUT} />
          </div>
          <div>
            <label style={MUTED_LABEL}>Route</label>
            <input type="text" value={workedForm.routeNumber} onChange={(event) => onWorkedFormChange('routeNumber', event.target.value)} placeholder="Route number" style={CONTROL_INPUT} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
            <div>
              <label style={MUTED_LABEL}>Deliveries</label>
              <input type="number" value={workedForm.deliveries} onChange={(event) => onWorkedFormChange('deliveries', event.target.value)} style={CONTROL_INPUT} />
            </div>
            <div>
              <label style={MUTED_LABEL}>Pickups</label>
              <input type="number" value={workedForm.pickups} onChange={(event) => onWorkedFormChange('pickups', event.target.value)} style={CONTROL_INPUT} />
            </div>
            <div>
              <label style={MUTED_LABEL}>Miles</label>
              <input type="number" value={workedForm.miles} onChange={(event) => onWorkedFormChange('miles', event.target.value)} style={CONTROL_INPUT} />
            </div>
            <div>
              <label style={MUTED_LABEL}>Truck #</label>
              <input type="text" value={workedForm.truckNumber} onChange={(event) => onWorkedFormChange('truckNumber', event.target.value)} style={CONTROL_INPUT} />
            </div>
          </div>
          <div>
            <label style={MUTED_LABEL}>Absence reason (if changing to absent)</label>
            <select value={absenceReason} onChange={(event) => onAbsenceReasonChange(event.target.value)} style={CONTROL_INPUT}>
              <option value="">Not absent</option>
              {ABSENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={MUTED_LABEL}>Notes</label>
            <textarea
              value={absenceNotes}
              onChange={(event) => onAbsenceNotesChange(event.target.value)}
              rows={2}
              placeholder="Optional"
              style={{ ...CONTROL_INPUT, resize: 'vertical' }}
            />
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onSubmitEdit}
              disabled={submitting}
              style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: C.gold, color: '#111', fontWeight: 700, cursor: submitting ? 'wait' : 'pointer' }}
            >
              {submitting ? 'Updating...' : 'Update entry'}
            </button>
            <button type="button" onClick={onClose} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: C.panel, color: C.text }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}

      {activeAction === 'absent' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <div>
            <label style={MUTED_LABEL}>Absence reason</label>
            <select value={absenceReason} onChange={(event) => onAbsenceReasonChange(event.target.value)} style={CONTROL_INPUT}>
              <option value="">Select a reason</option>
              {ABSENCE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={MUTED_LABEL}>Notes</label>
            <textarea
              value={absenceNotes}
              onChange={(event) => onAbsenceNotesChange(event.target.value)}
              rows={4}
              placeholder="Optional unless reason is Other"
              style={{ ...CONTROL_INPUT, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={onSubmitAbsent}
              disabled={submitting}
              style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: C.red, color: '#fff', fontWeight: 600, cursor: submitting ? 'wait' : 'pointer' }}
            >
              {submitting ? 'Saving...' : 'Record absence'}
            </button>
            <button type="button" onClick={onClose} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: C.panel, color: C.text }}>
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
